import { useEffect, useRef, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import { Spinner } from "@/components/Loader";
import { apiGet, apiPost } from "@/lib/api";
import { useEvent, useResyncOnVisible } from "@/lib/realtime";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

const SAFETY_POLL_MS = 5 * 60 * 1000;

export default function Messages() {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const t = await apiGet("/consumer/messages");
      setThread({ ...(t || {}), messages: Array.isArray(t?.messages) ? t.messages : [] });
      try { await apiPost("/consumer/messages/mark-read"); } catch {}
    } catch (err) {
      // 404 means no thread yet — represent as empty
      setThread({ messages: [] });
    }
  };

  useEffect(() => {
    load();
    // Safety-net poll only — WS is the primary signal.
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, SAFETY_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // Realtime: append in-place when a new message lands. Dedupe by id
  // in case the BE re-broadcasts.
  useEvent("message.new", (evt) => {
    if (!evt?.message?.id) return;
    setThread((prev) => {
      const cur = prev || { messages: [] };
      const list = Array.isArray(cur.messages) ? cur.messages : [];
      if (list.some((m) => (m._id || m.id) === evt.message.id)) return cur;
      return { ...cur, messages: [...list, evt.message] };
    });
    setPendingMessages((p) => p.filter((m) => m.text !== (evt.message.textContent || evt.message.text)));
    if (document.visibilityState === "visible") {
      apiPost("/consumer/messages/mark-read").catch(() => {});
    }
  });

  useResyncOnVisible(load);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread, pendingMessages]);

  const send = async (e) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    // Optimistic peach bubble — shows instantly with a 3-dot pulse footer
    // until the server confirms.
    const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setPendingMessages((p) => [...p, { id: tempId, sender: "consumer", text: trimmed, createdAt: new Date().toISOString() }]);
    setText("");
    try {
      await apiPost("/consumer/messages", { text: trimmed });
      // No forced refetch — WS broadcasts the server-confirmed message
      // back via `message.new` and the handler dedupes by id + drops
      // the optimistic bubble.
      setPendingMessages((p) => p.map((m) => m.id === tempId ? { ...m, deliveryStatus: "sent" } : m));
    } catch (err) {
      // Restore the input so the user can retry, and remove the optimistic bubble.
      setPendingMessages((p) => p.filter((m) => m.id !== tempId));
      setText(trimmed);
      toast(err.detail || "Failed to send", "error");
    } finally {
      setSending(false);
    }
  };

  if (!thread) return <div style={{ padding: 64, textAlign: "center" }}><Spinner /></div>;

  return (
    <div data-testid="messages-page" style={{ background: C.cream, minHeight: "calc(100vh - 80px)", display: "flex", flexDirection: "column" }}>
      <section style={{ background: C.ink, color: C.cream, padding: "48px 24px 64px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <Eyebrow color={C.peach}>★ CARE TEAM</Eyebrow>
          <Headline before="MESSAGE" italic="us." color={C.cream} italicColor={C.peach} size={56} className="mt-4" />
        </div>
      </section>

      <div
        ref={scrollRef}
        data-testid="messages-thread"
        style={{
          flex: 1,
          padding: "52px 24px 140px",
          overflowY: "auto",
          maxWidth: 800,
          margin: "0 auto",
          width: "100%",
          position: "relative",
          background: C.cream,
        }}
      >
        <TopWedge color={C.ink} dir="right" />
        <div style={{ position: "relative", zIndex: 2 }}>
        {thread.messages.length === 0 && pendingMessages.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: C.warmGray, fontSize: 14 }}>
            <p>Nothing yet. Send a message and we'll respond within 24 hours.</p>
          </div>
        )}
        {thread.messages.map((m) => {
          const me = m.sender === "consumer";
          return (
            <div key={m.id} data-testid={`msg-${m.sender}`} style={{ display: "flex", justifyContent: me ? "flex-end" : "flex-start", marginBottom: 12 }}>
              <div style={{ maxWidth: "78%", background: me ? C.peach : C.bone, color: C.ink, padding: "12px 16px", fontSize: 14, lineHeight: 1.5 }}>
                {!me && <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: C.peachDark, marginBottom: 4 }}>{m.senderName || "CARE TEAM"}</div>}
                <div>{m.text}</div>
                <div style={{ marginTop: 6, fontSize: 10, color: C.warmGray, textAlign: me ? "right" : "left" }}>
                  {new Date(m.createdAt).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
        {pendingMessages.map((m) => (
          <div key={m.id} data-testid="msg-pending" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <div style={{ maxWidth: "78%", background: C.peach, color: C.ink, padding: "12px 16px", fontSize: 14, lineHeight: 1.5 }}>
              <div>{m.text}</div>
              <div style={{ marginTop: 6, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
                <span aria-label="Sending" className="msg-sending-dots" style={{ display: "inline-flex", gap: 3 }}>
                  <span className="msg-dot" />
                  <span className="msg-dot" />
                  <span className="msg-dot" />
                </span>
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>

      <form
        onSubmit={send}
        data-testid="message-form"
        className="messages-input-bar"
        style={{
          background: C.pure,
          borderTop: `1px solid ${C.warmGray3}`,
          padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
          position: "fixed",
          left: 0,
          right: 0,
          // 64px = mobile bottom-nav height (AppShell). Desktop overrides to 0 via CSS.
          bottom: 64,
          zIndex: 40,
        }}
      >
        <div style={{ maxWidth: 800, margin: "0 auto", display: "flex", gap: 10 }}>
          <input
            data-testid="message-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message…"
            style={{
              flex: 1,
              padding: "14px 16px",
              background: C.cream,
              border: `1.5px solid ${C.warmGray3}`,
              fontSize: 16, // 16 prevents iOS Safari auto-zoom on focus
              color: C.ink,
              outline: "none",
              fontFamily: "inherit",
              minWidth: 0,
            }}
          />
          <Button type="submit" variant="primary" testId="message-send-btn" disabled={sending || !text.trim()}>
            {sending ? "…" : "SEND →"}
          </Button>
        </div>
      </form>
    </div>
  );
}
